import { StyleSheet } from 'react-native';
import type { TextStyle, ViewStyle } from 'react-native';
import { WARMUP_COLORS, WORDLE_KEY_COLORS } from '../../../src/quiz/constants/warmup.constants';

export type WordleKeyState = keyof typeof WORDLE_KEY_COLORS;

export const wordleKeyboardStyles = StyleSheet.create({
  container: {
    paddingHorizontal: 6,
    paddingTop: 4,
    paddingBottom: 12,
    backgroundColor: WARMUP_COLORS.bg,
  },
  row: {
    flexDirection: 'row',
    gap: 5,
    justifyContent: 'center',
    marginBottom: 5,
  },
  // Outer wrapper paints the 3px chip ledge; the flat face sits on top and leaves it exposed.
  keyOuter: {
    flexBasis: 0,
    flexShrink: 1,
    flexGrow: 1,
    height: 46,
    borderRadius: 8,
    justifyContent: 'flex-start',
  },
  // ENTER/DEL keys are wider than letter keys.
  keyOuterAction: {
    flexGrow: 1.5,
  },
  keyFace: {
    width: '100%',
    height: 43,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  keyText: {
    fontWeight: '700',
    textTransform: 'uppercase',
    fontSize: 15,
  },
  keyTextAction: {
    fontSize: 11,
  },
  pressed: {
    opacity: 0.7,
  },
});

// Per-state key palette as named styles (selected by the finite WordleKeyState), so the component
// never builds inline color objects. Single-sourced from WORDLE_KEY_COLORS.
const KEY_STATES = Object.keys(WORDLE_KEY_COLORS) as WordleKeyState[];

const buildKeyStyles = <T extends ViewStyle | TextStyle>(
  pick: (state: WordleKeyState) => T,
): Record<WordleKeyState, T> =>
  StyleSheet.create(
    Object.fromEntries(KEY_STATES.map((state) => [state, pick(state)])) as Record<WordleKeyState, T>,
  );

/** Outer ledge color per key state. */
export const wordleKeyLedgeStyles = buildKeyStyles<ViewStyle>((state) => ({
  backgroundColor: WORDLE_KEY_COLORS[state].ledge,
}));

/** Face background per key state. */
export const wordleKeyFaceStyles = buildKeyStyles<ViewStyle>((state) => ({
  backgroundColor: WORDLE_KEY_COLORS[state].bg,
}));

/** Key label color per key state. */
export const wordleKeyTextStyles = buildKeyStyles<TextStyle>((state) => ({
  color: WORDLE_KEY_COLORS[state].text,
}));
